import React, { useMemo, useState } from 'react';

import {
    Card,
    Empty,
    Pagination,
    Space,
    Spin,
    Tag,
    Typography
} from 'antd';

import {
    buildStatusColorMap,
    formatMoscowDateTime,
    normalizeStatusKey
} from '../utils/taskManager';

import './TaskManagerList.css';

const { Text, Title } = Typography;

const StatusTag = ({ status, colorMap }) => {
    const color =
        colorMap[normalizeStatusKey(status)] || '#1677ff';

    return (
        <Tag
            style={{
                margin: 0,
                color: '#fff',
                background: color,
                borderColor: color,
                fontWeight: 600
            }}
        >
            {status || 'Без статуса'}
        </Tag>
    );
};

const cfValue = (task, name) => {
    const field = (task?.fields || []).find(
        item => item.system_name === name
    );
    return field?.value;
};

const MetaItem = ({ label, value }) => (
    <div className="tm-list-meta-item">
        <Text type="secondary" className="tm-list-meta-label">
            {label}
        </Text>
        <Text className="tm-list-meta-value" ellipsis>
            {value || '—'}
        </Text>
    </div>
);

const TaskManagerList = ({
    tasks = [],
    statuses = [],
    colorMap: colorMapProp,
    loading = false,
    onTaskClick
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const colorMap = useMemo(
        () => colorMapProp || buildStatusColorMap(statuses),
        [colorMapProp, statuses]
    );

    const sortedTasks = useMemo(
        () =>
            [...tasks].sort(
                (a, b) => Number(b.id) - Number(a.id)
            ),
        [tasks]
    );

    const maxPage = Math.max(
        1,
        Math.ceil(sortedTasks.length / pageSize) || 1
    );
    const safePage = Math.min(page, maxPage);

    const pageTasks = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return sortedTasks.slice(start, start + pageSize);
    }, [sortedTasks, safePage, pageSize]);

    if (!tasks.length && !loading) {
        return <Empty description="Ничего не найдено" />;
    }

    return (
        <Spin spinning={loading} wrapperClassName="tm-list-spin">
            <div className="tm-list">
                <div className="tm-list-stack">
                    {pageTasks.map(task => {
                        const tt =
                            cfValue(task, 'Nomer_TT') ||
                            task.task_number ||
                            '—';
                        const bs = cfValue(task, 'Nomer_BS') || '—';

                        return (
                            <Card
                                key={task.id}
                                size="small"
                                hoverable
                                className="tm-list-card"
                                onClick={() => onTaskClick?.(task)}
                            >
                                <div className="tm-list-card-head">
                                    <Space wrap size={[8, 6]}>
                                        <Text
                                            type="secondary"
                                            strong
                                        >
                                            #{task.id}
                                        </Text>
                                        <StatusTag
                                            status={task.status_name}
                                            colorMap={colorMap}
                                        />
                                    </Space>
                                    <Text type="secondary">
                                        {formatMoscowDateTime(
                                            task.created_at
                                        )}
                                    </Text>
                                </div>

                                <Title
                                    level={5}
                                    className="tm-list-card-title"
                                >
                                    {task.title || 'Без названия'}
                                </Title>

                                <div className="tm-list-meta-grid">
                                    <MetaItem label="ТТ" value={tt} />
                                    <MetaItem label="БС" value={bs} />
                                    <MetaItem
                                        label="Исполнитель"
                                        value={
                                            task.assigned_user_fio
                                        }
                                    />
                                    <MetaItem
                                        label="Заказчик"
                                        value={task.region_name}
                                    />
                                </div>
                            </Card>
                        );
                    })}
                </div>

                {sortedTasks.length > 0 ? (
                    <div className="tm-list-pagination">
                        <Pagination
                            current={safePage}
                            pageSize={pageSize}
                            total={sortedTasks.length}
                            showSizeChanger
                            pageSizeOptions={[
                                '25',
                                '50',
                                '100',
                                '200'
                            ]}
                            showTotal={total => (
                                <Text type="secondary">
                                    {total.toLocaleString('ru-RU')}{' '}
                                    записей
                                </Text>
                            )}
                            onChange={(nextPage, nextSize) => {
                                setPage(nextPage);
                                setPageSize(nextSize);
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </Spin>
    );
};

export default TaskManagerList;
